<template>
  <component
    :is="asForm ? 'form' : 'div'"
    class="ob-frame"
    tabindex="-1"
    @submit.prevent="$emit('submit')"
  >
    <!-- The one layout every onboarding page shares, in the Save to list dialog's look:
         a top row (Back · step bar · Close, or the Sign up / Log in tabs), the title,
         the page's content and a footer for its buttons. Rendered as a <form> when the
         page submits, so Enter and the footer's submit button both work. -->
    <div class="ob-handle" aria-hidden="true"></div>

    <div v-if="$slots.nav" class="ob-nav ob-nav--tabs">
      <slot name="nav" />
      <span class="ob-nav__grow"></span>
      <button
        v-if="closable"
        class="ob-icon-btn ob-close"
        type="button"
        data-bs-dismiss="modal"
        :aria-label="$t('shared.close')"
      >
        <span class="ms" aria-hidden="true">close</span>
      </button>
    </div>
    <div v-else class="ob-nav">
      <button
        v-if="back"
        class="ob-icon-btn ob-back"
        type="button"
        :aria-label="$t('shared.back')"
        @click="$emit('back')"
      >
        <span class="ms" aria-hidden="true">arrow_back</span>
      </button>
      <!-- A missing end keeps its 34 px so the step bar stays centred. -->
      <span v-else-if="step" class="ob-nav__gap"></span>
      <OnBoardingStepIndicator v-if="step" :current="step" />
      <span v-else class="ob-nav__grow"></span>
      <button
        v-if="closable"
        class="ob-icon-btn ob-close"
        type="button"
        data-bs-dismiss="modal"
        :aria-label="$t('shared.close')"
      >
        <span class="ms" aria-hidden="true">close</span>
      </button>
      <span v-else-if="step" class="ob-nav__gap"></span>
    </div>

    <div class="ob-body">
      <div v-if="error" class="ob-error" role="alert">{{ error }}</div>
      <div v-if="notice" class="ob-notice" role="status">{{ notice }}</div>
      <div v-if="title || $slots.title" class="ob-heading">
        <h2 id="onBoardingTitle" class="ob-title"><slot name="title">{{ title }}</slot></h2>
        <p v-if="subtitle" class="ob-subtitle">{{ subtitle }}</p>
      </div>
      <slot />
    </div>

    <div v-if="$slots.footer" class="ob-foot" :class="{ 'ob-foot--two': splitFooter }">
      <slot name="footer" />
    </div>
  </component>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import OnBoardingStepIndicator from './OnBoardingStepIndicator.vue';
import '../onboarding.css';

export default defineComponent({
  name: 'OnBoardingFrame',
  components: { OnBoardingStepIndicator },
  props: {
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    // 1–8 for the signup steps; 0 hides the step bar.
    step: { type: Number, default: 0 },
    back: { type: Boolean, default: false },
    error: { type: String, default: null },
    notice: { type: String, default: null },
    asForm: { type: Boolean, default: false },
    // Two footer buttons side by side (Skip · Continue).
    splitFooter: { type: Boolean, default: false },
  },
  emits: ['back', 'submit'],
  computed: {
    // The standalone /login page has nothing to close.
    closable(): boolean {
      return !this.$store?.state?.onBoarding?.standaloneMode;
    },
  },
  mounted() {
    // Pages swap with v-if, which drops focus to <body>, and Bootstrap only closes
    // the modal on Esc while focus is inside it. Keep focus in the dialog; a page
    // that focuses its own first field does so after this.
    const root = this.$el as HTMLElement;
    if (root.closest?.('.modal.show') && !root.contains(document.activeElement)) {
      root.focus({ preventScroll: true });
    }
  },
});
</script>
